import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, Sparkles, Trash2, BarChart3 } from 'lucide-react';
import './PersistentChat.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PersistentChat({ authToken, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadHistory();
    loadStats();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/persistent-chat/history`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { limit: 200 }
      });
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/persistent-chat/stats`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    // Optimistically add user message to UI
    const tempUserMsg = {
      message: userMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(
        `${API}/persistent-chat/send`,
        { message: userMessage },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // Add AI response
      const aiMsg = {
        message: response.data.ai_response,
        sender: 'ai',
        timestamp: response.data.timestamp
      };
      setMessages(prev => [...prev, aiMsg]);
      
      // Refresh stats
      loadStats();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
      // Remove the optimistic message
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all conversation history? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/persistent-chat/clear`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setMessages([]);
      loadStats();
    } catch (error) {
      console.error('Failed to clear history:', error);
      alert('Failed to clear history. Please try again.');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="persistent-chat-overlay">
      <div className="persistent-chat-container">
        {/* Header */}
        <div className="chat-header">
          <div className="header-left">
            <Sparkles size={24} color="#a78bfa" />
            <div>
              <h2 className="chat-title">Memory Chat</h2>
              <p className="chat-subtitle">Every conversation saved forever</p>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="icon-btn"
              onClick={() => setShowStats(!showStats)}
              title="View Statistics"
            >
              <BarChart3 size={20} />
            </button>
            <button
              className="icon-btn"
              onClick={clearHistory}
              title="Clear History"
            >
              <Trash2 size={20} />
            </button>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Stats Panel */}
        {showStats && stats && (
          <div className="stats-panel">
            <div className="stat-item">
              <span className="stat-label">Total Messages:</span>
              <span className="stat-value">{stats.total_messages}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Your Messages:</span>
              <span className="stat-value">{stats.user_messages}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">AI Messages:</span>
              <span className="stat-value">{stats.ai_messages}</span>
            </div>
            {stats.first_conversation && (
              <div className="stat-item">
                <span className="stat-label">First Conversation:</span>
                <span className="stat-value">
                  {new Date(stats.first_conversation).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="empty-state">
              <Sparkles size={48} color="#a78bfa" />
              <h3>Start Your First Conversation</h3>
              <p>Every message you share here will be saved permanently.</p>
              <p>Come back anytime - your conversation history will always be here.</p>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`message ${msg.sender === 'user' ? 'user-message' : 'ai-message'}`}
                >
                  <div className="message-content">
                    <div className="message-text">{msg.message}</div>
                    <div className="message-timestamp">
                      {formatTimestamp(msg.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="message ai-message">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="chat-input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Share your thoughts... (Press Enter to send)"
            className="chat-input"
            disabled={loading}
            rows={2}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !inputMessage.trim()}
            className="send-btn"
          >
            <Send size={20} />
          </button>
        </div>

        {/* Info Footer */}
        <div className="chat-footer">
          💾 All messages auto-saved • {messages.length} messages in history
        </div>
      </div>
    </div>
  );
}

export default PersistentChat;
